// Recipe → timeline compiler, plus the pure samplers the player uses every
// frame: sampleCamera() (where the camera is at time t) and visualStateAt()
// (letterbox, banner, particles, golden light, bloom, fade at time t).
//
// Camera steps become contiguous segments from 0 to the total duration;
// screen steps become cues at the moment they appear in the recipe. With
// `reducedMotion` every camera move collapses into one still framing (the
// first shot the recipe would have flown to) and the cinematic fades in
// from black instead; particles and bloom flashes are dropped.

import type { CameraPose, CopyRef, ParticleKind, Recipe, Vec3 } from './types.js';

export interface TimelineContext {
  /** Camera pose when the cinematic starts. */
  readonly startPose: CameraPose;
  /** The milestone's world position, if it has one. */
  readonly focus: Vec3 | null;
  /** The local player's capital. */
  readonly capital: Vec3 | null;
  /** Bounding circle of the local player's cities (for pullBack). */
  readonly empire: { readonly center: Vec3; readonly radius: number } | null;
  /** Earlier big moments to cut back to in the finale (last 3 are used). */
  readonly highlights: ReadonlyArray<Vec3>;
  readonly reducedMotion: boolean;
}

export type CameraSegment =
  | {
      readonly kind: 'move';
      readonly startMs: number;
      readonly endMs: number;
      readonly from: CameraPose;
      readonly to: CameraPose;
      /** Extra height at the middle of the move, so long flights arc. */
      readonly lift: number;
    }
  | {
      readonly kind: 'orbit';
      readonly startMs: number;
      readonly endMs: number;
      readonly center: Vec3;
      readonly radius: number;
      readonly height: number;
      readonly yawFrom: number;
      readonly yawTo: number;
      /** Look-at point sits this far from the centre, toward the camera. */
      readonly lead: number;
    }
  | {
      readonly kind: 'still';
      readonly startMs: number;
      readonly endMs: number;
      readonly pose: CameraPose;
    };

export type Cue =
  | { readonly atMs: number; readonly kind: 'letterbox'; readonly on: boolean }
  | {
      readonly atMs: number;
      readonly kind: 'banner';
      readonly title: CopyRef;
      readonly subtitle?: CopyRef;
    }
  | { readonly atMs: number; readonly kind: 'particles'; readonly particles: ParticleKind }
  | { readonly atMs: number; readonly kind: 'golden' }
  | { readonly atMs: number; readonly kind: 'bloomPulse' }
  | { readonly atMs: number; readonly kind: 'sting'; readonly soundId: string };

export interface Timeline {
  readonly durationMs: number;
  readonly camera: ReadonlyArray<CameraSegment>;
  readonly cues: ReadonlyArray<Cue>;
  readonly reducedMotion: boolean;
}

/** Fade to black over the last few ms, which hides the jump back to the saved pose. */
export const FADE_OUT_MS = 320;
/** Reduced motion: fade in from black onto the still framing. */
export const FADE_IN_MS = 450;
const GOLDEN_RAMP_MS = 800;
const BLOOM_PULSE_MS = 900;

// ── pose math ─────────────────────────────────────────────────────────────

const DEG = Math.PI / 180;

/** Heading of the camera around its target (0 = looking north from the south). */
export function yawOf(pose: CameraPose): number {
  return Math.atan2(pose.position[0] - pose.target[0], pose.position[2] - pose.target[2]);
}

/**
 * How far short of a subject (toward the camera) to aim so the subject sits
 * `raiseDeg` above the centre of the frame, clear of the centred banner.
 */
export function frameLead(height: number, pitchDeg: number, raiseDeg: number): number {
  const p = clampPitch(pitchDeg);
  const look = clampPitch(pitchDeg + raiseDeg);
  return height / Math.tan(p) - height / Math.tan(look);
}

function clampPitch(deg: number): number {
  return Math.min(85, Math.max(5, deg)) * DEG;
}

/**
 * Camera `height` above `target`, looking down at `pitchDeg`, from heading
 * `yaw`. With `raiseDeg` it aims a little short of the target so the target
 * appears that many degrees above the centre of the frame.
 */
export function poseLookingAt(
  target: Vec3,
  height: number,
  pitchDeg: number,
  yaw: number,
  raiseDeg = 0,
): CameraPose {
  const d = height / Math.tan(clampPitch(pitchDeg));
  const lead = raiseDeg ? frameLead(height, pitchDeg, raiseDeg) : 0;
  const sx = Math.sin(yaw);
  const sz = Math.cos(yaw);
  return {
    position: [target[0] + sx * d, target[1] + height, target[2] + sz * d],
    target: [target[0] + sx * lead, target[1], target[2] + sz * lead],
  };
}

/** Framed subjects sit this many degrees above the centre of the frame. */
export const FRAME_RAISE_DEG = 9;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpVec(a: Vec3, b: Vec3, t: number): Vec3 {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const easeInOutSine = (t: number) => -(Math.cos(Math.PI * t) - 1) / 2;

// ── compiler ──────────────────────────────────────────────────────────────

export function buildTimeline(recipe: Recipe, ctx: TimelineContext): Timeline {
  const camera: CameraSegment[] = [];
  const cues: Cue[] = [];
  let t = 0;
  let pose = ctx.startPose;

  const resolve = (at: Vec3 | 'focus' | 'capital'): Vec3 => {
    if (at === 'focus') return ctx.focus ?? ctx.capital ?? ctx.startPose.target;
    if (at === 'capital') return ctx.capital ?? ctx.focus ?? ctx.startPose.target;
    return at;
  };
  const moveTo = (to: CameraPose, ms: number) => {
    const dist = Math.hypot(
      to.position[0] - pose.position[0],
      to.position[1] - pose.position[1],
      to.position[2] - pose.position[2],
    );
    camera.push({
      kind: 'move',
      startMs: t,
      endMs: t + ms,
      from: pose,
      to,
      lift: Math.min(6, dist * 0.15),
    });
    pose = to;
    t += ms;
  };

  for (const step of recipe) {
    switch (step.kind) {
      case 'flyTo':
        moveTo(
          poseLookingAt(resolve(step.at), step.height, step.pitch, yawOf(pose), FRAME_RAISE_DEG),
          step.ms,
        );
        break;
      case 'orbit': {
        const c = resolve(step.at);
        const dx = pose.position[0] - c[0];
        const dz = pose.position[2] - c[2];
        const yawFrom = Math.atan2(dx, dz);
        const yawTo = yawFrom + step.degrees * DEG;
        const radius = Math.hypot(dx, dz);
        const height = pose.position[1] - c[1];
        const lead = Math.hypot(pose.target[0] - c[0], pose.target[2] - c[2]);
        const seg = {
          kind: 'orbit' as const,
          startMs: t,
          endMs: t + step.ms,
          center: c,
          radius,
          height,
          yawFrom,
          yawTo,
          lead,
        };
        camera.push(seg);
        pose = sampleSegment(seg, seg.endMs);
        t += step.ms;
        break;
      }
      case 'pullBack': {
        const center = ctx.empire?.center ?? ctx.capital ?? ctx.startPose.target;
        const radius = ctx.empire?.radius ?? 8;
        const height = Math.min(42, Math.max(16, radius * 1.4 + 12));
        moveTo(poseLookingAt(center, height, 52, yawOf(pose), 6), step.ms);
        break;
      }
      case 'highlights': {
        // Quick cuts: jump to each moment and push in a little.
        for (const h of ctx.highlights.slice(-3)) {
          const yaw = yawOf(pose);
          const from = poseLookingAt(h, 7.5, 40, yaw, FRAME_RAISE_DEG);
          const to = poseLookingAt(h, 5.5, 34, yaw, FRAME_RAISE_DEG);
          camera.push({ kind: 'move', startMs: t, endMs: t + step.ms, from, to, lift: 0 });
          pose = to;
          t += step.ms;
        }
        break;
      }
      case 'hold':
        camera.push({ kind: 'still', startMs: t, endMs: t + step.ms, pose });
        t += step.ms;
        break;
      case 'letterbox':
        cues.push({ atMs: t, kind: 'letterbox', on: step.on });
        break;
      case 'banner':
        cues.push(
          step.subtitle
            ? { atMs: t, kind: 'banner', title: step.title, subtitle: step.subtitle }
            : { atMs: t, kind: 'banner', title: step.title },
        );
        break;
      case 'particles':
        if (!ctx.reducedMotion)
          cues.push({ atMs: t, kind: 'particles', particles: step.particles });
        break;
      case 'timeOfDay':
        cues.push({ atMs: t, kind: 'golden' });
        break;
      case 'bloomPulse':
        if (!ctx.reducedMotion) cues.push({ atMs: t, kind: 'bloomPulse' });
        break;
      case 'sting':
        cues.push({ atMs: t, kind: 'sting', soundId: step.soundId });
        break;
    }
  }

  if (!ctx.reducedMotion) return { durationMs: t, camera, cues, reducedMotion: false };

  // Reduced motion: one still framing — the first shot the recipe flies to.
  const first = camera.find((s) => s.kind !== 'still');
  const still =
    first?.kind === 'move'
      ? first.to
      : first?.kind === 'orbit'
        ? sampleSegment(first, first.startMs)
        : ctx.startPose;
  return {
    durationMs: t,
    camera: t > 0 ? [{ kind: 'still', startMs: 0, endMs: t, pose: still }] : [],
    cues,
    reducedMotion: true,
  };
}

// ── samplers ──────────────────────────────────────────────────────────────

function sampleSegment(seg: CameraSegment, t: number): CameraPose {
  const span = Math.max(1, seg.endMs - seg.startMs);
  const u = Math.min(1, Math.max(0, (t - seg.startMs) / span));
  switch (seg.kind) {
    case 'still':
      return seg.pose;
    case 'move': {
      const e = easeInOut(u);
      const p = lerpVec(seg.from.position, seg.to.position, e);
      const arc = Math.sin(Math.PI * e) * seg.lift;
      return {
        position: [p[0], p[1] + arc, p[2]],
        target: lerpVec(seg.from.target, seg.to.target, e),
      };
    }
    case 'orbit': {
      const yaw = lerp(seg.yawFrom, seg.yawTo, easeInOutSine(u));
      const c = seg.center;
      const sx = Math.sin(yaw);
      const sz = Math.cos(yaw);
      return {
        position: [c[0] + sx * seg.radius, c[1] + seg.height, c[2] + sz * seg.radius],
        target: [c[0] + sx * seg.lead, c[1], c[2] + sz * seg.lead],
      };
    }
  }
}

/** Camera pose at `t` ms, or null when the timeline has no camera work. */
export function sampleCamera(tl: Timeline, t: number): CameraPose | null {
  if (tl.camera.length === 0) return null;
  for (const seg of tl.camera) {
    if (t < seg.endMs) return sampleSegment(seg, t);
  }
  const last = tl.camera[tl.camera.length - 1] as CameraSegment;
  return sampleSegment(last, last.endMs);
}

export interface VisualState {
  readonly letterbox: boolean;
  readonly banner: { readonly title: CopyRef; readonly subtitle?: CopyRef } | null;
  readonly particles: ParticleKind | null;
  /** 0..1 strength of the golden-hour grade. */
  readonly golden: number;
  /** 0..1 bloom flash. */
  readonly bloom: number;
  /** 0..1 black overlay (fade in / out). */
  readonly fade: number;
}

export function visualStateAt(tl: Timeline, t: number): VisualState {
  let letterbox = false;
  let banner: VisualState['banner'] = null;
  let particles: ParticleKind | null = null;
  let goldenAt: number | null = null;
  let bloom = 0;
  for (const cue of tl.cues) {
    if (cue.atMs > t) continue;
    switch (cue.kind) {
      case 'letterbox':
        // The closing letterbox cue sits at the very end; bars stay up until then.
        if (cue.on || cue.atMs < tl.durationMs) letterbox = cue.on;
        break;
      case 'banner':
        banner = cue.subtitle ? { title: cue.title, subtitle: cue.subtitle } : { title: cue.title };
        break;
      case 'particles':
        particles = cue.particles;
        break;
      case 'golden':
        goldenAt ??= cue.atMs;
        break;
      case 'bloomPulse': {
        const u = (t - cue.atMs) / BLOOM_PULSE_MS;
        if (u >= 0 && u <= 1) bloom = Math.max(bloom, Math.sin(Math.PI * u));
        break;
      }
    }
  }
  const remaining = tl.durationMs - t;
  let golden = 0;
  if (goldenAt !== null) {
    golden = Math.min(1, (t - goldenAt) / GOLDEN_RAMP_MS, Math.max(0, remaining) / GOLDEN_RAMP_MS);
  }
  let fade = 0;
  if (remaining < FADE_OUT_MS) fade = 1 - Math.max(0, remaining) / FADE_OUT_MS;
  if (tl.reducedMotion && t < FADE_IN_MS) fade = Math.max(fade, 1 - t / FADE_IN_MS);
  return { letterbox, banner, particles, golden: Math.max(0, golden), bloom, fade };
}
